// modules/requerimiento/pages/RequerimientoDetalle.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, CircularProgress, Divider, IconButton, Tooltip, Typography,
  Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableRow, TableCell, Chip, Container,
} from "@mui/material";
import ReplayIcon       from "@mui/icons-material/Replay";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import ContentCopyIcon  from "@mui/icons-material/ContentCopy";
import LaunchIcon       from "@mui/icons-material/Launch";
import EditIcon         from "@mui/icons-material/Edit";
import TodayIcon        from "@mui/icons-material/Today";
import ScheduleIcon     from "@mui/icons-material/Schedule";
import EngineeringIcon  from "@mui/icons-material/Engineering";
import PersonIcon       from "@mui/icons-material/Person";
import PhoneIcon        from "@mui/icons-material/Phone";
import NoteAltIcon      from "@mui/icons-material/NoteAlt";
import WhatsAppIcon     from "@mui/icons-material/WhatsApp";
import Timeline               from "@mui/lab/Timeline";
import TimelineItem           from "@mui/lab/TimelineItem";
import TimelineSeparator      from "@mui/lab/TimelineSeparator";
import TimelineDot            from "@mui/lab/TimelineDot";
import TimelineConnector      from "@mui/lab/TimelineConnector";
import TimelineContent        from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";

import { useParams, useNavigate }       from "react-router-dom";
import DashboardLayout                  from "../../../layouts/DashboardLayout";
import debounce                         from "lodash.debounce";
import { getViewFor }                   from "../views";
import ConfirmarTransferencia           from "../views/devolucion/ConfirmarTransferencia";
import EditCoordinacionDialog           from "../components/EditCoordinacionDialog";

import {
  getRequerimiento, updateRequerimiento, updateEstado,
  getHistory, updateCoordinacion, getUsuariosTrs,
  getClienteServicio, getTercializados, getCatalogoPlacasVehiculos,
  getAsignacionesModems,
} from "../services/reqSolicitudes";
import { getProfile } from "../services/auth";

// ─────────────────────────────────────────────
// CONSTANTES Y HELPERS (fuera del componente)
// ─────────────────────────────────────────────

const estadoMap = {
  0: { label: "Eliminado",           color: "default"   },
  1: { label: "Coordinación",        color: "warning"   },
  2: { label: "Programación",        color: "info"      },
  3: { label: "Trabajo de campo",    color: "primary"   },
  4: { label: "Trabajo de campo",    color: "primary"   },
  7: { label: "Revisión en Sistemas",color: "secondary" },
  8: { label: "Finalizado",          color: "success"   },
};

// 1234-ABC, ABC-123 o sin guion: BD09250 (B + letra + 5 números) y BE123/BE1234.
const PLATE_REGEX = /^([A-Z]{2,4}-\d{3,4}|\d{3,4}-[A-Z]{2,4}|B[A-Z]\d{5}|BE\d{3,4})$/;

const HISTORY_TIPO_COLOR = {
  CREACION:     "secondary",
  ESTADO:       "primary",
  COORDINACION: "warning",
  EQUIPO:       "info",
  SISTEMA:      "success",
};

function isBogusDate(iso) {
  if (!iso) return true;
  const d = new Date(iso);
  return isNaN(d.getTime()) || d.getFullYear() < 1970;
}
function fmtFecha(iso) {
  if (isBogusDate(iso)) return "—";
  return new Date(iso).toLocaleDateString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function fmtHora(hhmmss) {
  if (!hhmmss) return "—";
  const [hh, mm] = String(hhmmss).split(":");
  return hh && mm ? `${hh}:${mm}` : hhmmss;
}
function isoDateToYMD(iso) {
  if (isBogusDate(iso)) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const normalizePlate = (s) => (s || "").trim().replace(/\s+/g, "").toUpperCase();
const isValidPlate   = (p) => PLATE_REGEX.test(p);
// Placa escrita a mano: "1234abc" o "1234 ABC" -> "1234-ABC"; "abc123" -> "ABC-123";
// "bd-09250" -> "BD09250" (ese formato va sin guion).
const normalizeNewPlate = (s) => {
  const p = normalizePlate(s).replace(/-/g, "");
  if (/^(B[A-Z]\d{5}|BE\d{3,4})$/.test(p)) return p;
  const m = p.match(/^(\d{3,4})([A-Z]{2,4})$/) || p.match(/^([A-Z]{2,4})(\d{3,4})$/);
  return m ? `${m[1]}-${m[2]}` : normalizePlate(s);
};

// FIX: Row definido FUERA del componente
function Row({ label, value, icon }) {
  return (
    <TableRow>
      <TableCell sx={{ width: { xs: 120, sm: 150 }, fontWeight: 700, fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>{icon}{label}</Box>
      </TableCell>
      <TableCell sx={{ p: 0 }}>
        <Typography noWrap sx={{ fontSize: 12, lineHeight: 1.25, px: 1 }}>{value ?? "—"}</Typography>
      </TableCell>
    </TableRow>
  );
}

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────

export default function RequerimientoDetalle({ codProp, embedded = false, onBack }) {
  const { cod: routeCod }  = useParams();
  const cod = codProp || routeCod;
  const navigate = useNavigate();
  const handleBack = onBack || (() => navigate(-1));

  // ── Datos principales ──────────────────────
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);   // FIX: estado de error visible
  const [history, setHistory] = useState([]);

  // ── UI ─────────────────────────────────────
  const [refreshKey,   setRefreshKey]   = useState(0);
  const [openEdit,     setOpenEdit]     = useState(false);
  const [openDetails,  setOpenDetails]  = useState(false);
  const [openFinalizar,setOpenFinalizar]= useState(false); // FIX: Dialog en lugar de window.confirm
  const [saving,       setSaving]       = useState(false);
  const [finalizando,  setFinalizando]  = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const handleViewToast = useCallback((opts) => {
    setToast({ open: true, ...opts });
  }, []);

  // ── Coordinación ───────────────────────────
  const [coordId,     setCoordId]     = useState("");
  const [form,        setForm]        = useState({ observacion: "", fecha: "", hora: "", lugar: "", contacto: "", telefono: "" });
  const [placaInput,  setPlacaInput]  = useState("");
  const [placas,      setPlacas]      = useState([]);
  const [tecnicos,    setTecnicos]    = useState([]);
  const [tecnicosSel, setTecnicosSel] = useState([]);

  // ── Placas del cliente ─────────────────────
  const [placasCliente,        setPlacasCliente]        = useState([]);
  const [placasClienteLoading, setPlacasClienteLoading] = useState(false);

  // ── Tercializado ───────────────────────────
  const [tercializado, setTercializado] = useState(false);
  const [tercSelected, setTercSelected] = useState(null);
  const [tercOptions,  setTercOptions]  = useState([]);
  const [tercLoading,  setTercLoading]  = useState(false);
  const [tercError,    setTercError]    = useState(false);
  const [terciaInfo,   setTerciaInfo]   = useState(null);
  const [terciaLoading,setTerciaLoading]= useState(false);
  // Si ya se adicionaron equipos, no se puede cambiar el cliente tercializado
  // (cambiaria el cliente al que quedaron asignados los modems).
  const [tieneEquiposAsignados, setTieneEquiposAsignados] = useState(false);

  // ── Perfil ─────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);

  // ─────────────────────────────────────────────
  // DERIVADOS
  // ─────────────────────────────────────────────

  const estadoNum  = Number(data?.estado);
  const isEliminado= estadoNum === 0;
  const esAdicion  = useMemo(() => /adici/i.test(String(data?.nombre_ot_tipo || "")), [data?.nombre_ot_tipo]);

  const estadoInfo = useMemo(() => {
    return estadoMap[estadoNum] || { label: String(data?.estado || "—"), color: "default" };
  }, [data, estadoNum]);

  const isTercializado = useMemo(() => {
    const flag = data?.tercializado === true || data?.tercializado === 1 || data?.tercializado === "1";
    return flag || !!(data?.tercia ?? data?.cod_servicio_cliente_tercializado);
  }, [data]);

  const phoneValid = useMemo(() => !form.telefono || /^\+?\d{7,}$/.test(form.telefono), [form.telefono]);
  const invalids   = useMemo(() => placas.filter((p) => !isValidPlate(p)), [placas]);
  const canSave    = useMemo(() => !!coordId && !!form.fecha && !!form.hora && !saving, [coordId, form, saving]);

  const currentUsername = useMemo(() => {
    const u = currentUser;
    return u?.username ?? u?.user?.username ?? u?.usu ?? u?.login ?? u?.email ?? null;
  }, [currentUser]);

  const isCreator = useMemo(() => {
    if (!data) return false;
    const reqUser = data?.usu ?? data?.usuario ?? data?.username ?? null;
    if (!reqUser || !currentUsername) return false;
    return String(reqUser).toLowerCase() === String(currentUsername).toLowerCase();
  }, [data, currentUsername]);

  const requierePlacasCliente = useMemo(() => {
    const idTipo = String(
      data?.tipo_req ?? data?.id_ot_tipo ?? data?.id_tipo ?? ""
    ).trim();
    if (idTipo && ["2", "5"].includes(idTipo)) return true;
    return /mantenim|devoluci/.test(String(data?.nombre_ot_tipo ?? "").toLowerCase());
  }, [data?.tipo_req, data?.id_ot_tipo, data?.id_tipo, data?.nombre_ot_tipo]);

  const tercValue = (() => {
    const isT = isTercializado || tercializado;
    if (!isT) return "No";
    if (terciaLoading) return "Cargando…";
    if (terciaInfo) return `${terciaInfo?.nombre_cliente ?? "—"} (#${terciaInfo?.cod_servicio_cliente ?? "—"})`;
    const id = data?.tercia ?? data?.cod_servicio_cliente_tercializado;
    return id ? `Sí (#${id})` : "Sí";
  })();

  // ─────────────────────────────────────────────
  // LOADERS
  // ─────────────────────────────────────────────

  const fetchProfile = useCallback(async () => {
    try { const resp = await getProfile(); setCurrentUser(resp || null); }
    catch { setCurrentUser(null); }
  }, []);

  const fetchTecnicos = useCallback(async (suc) => {
    try {
      const sucursales = String(suc || "").trim().toLowerCase() === "la paz"
        ? "La Paz,La Paz(El Alto)"
        : suc;
      const resp = await getUsuariosTrs({ role: "tecnico", sucursal: sucursales });
      const list = (resp?.data ?? []).filter((t) => t.estado === "A");
      list.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" }));
      setTecnicos(list);
    } catch { setTecnicos([]); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try { const resp = await getHistory(cod); setHistory(resp?.data || []); }
    catch { setHistory([]); }
  }, [cod]);

  const fetchTerciaInfo = useCallback(async (id) => {
    if (!id) { setTerciaInfo(null); return; }
    try {
      setTerciaLoading(true);
      const { data: cs } = await getClienteServicio(id);
      setTerciaInfo(cs || null);
    } finally { setTerciaLoading(false); }
  }, []);

  const fetchTercializados = useMemo(() =>
    debounce(async (texto) => {
      if (!data?.cod_servicio_cliente) return;
      try {
        setTercLoading(true);
        const { data: resp } = await getTercializados(String(data.cod_servicio_cliente), texto || "", 20);
        const arr = Array.isArray(resp) ? resp : [];
        setTercOptions(arr.map((o) => ({
          cod_servicio: String(o.cod_servicio ?? o.cod_servicio_cliente ?? o.id ?? ""),
          razon_social: String(o.razon_social ?? o.nombre ?? o.nom ?? ""),
          descripcion: o.descripcion ?? "",
          tipo: o.tipo ?? "",
          sucursal: o.sucursal ?? o.sucursal_facturar ?? "",
        })));
      } finally { setTercLoading(false); }
    }, 300),
  [data?.cod_servicio_cliente]);

  useEffect(() => () => fetchTercializados.cancel?.(), [fetchTercializados]);

  // Aplica los datos del requerimiento al formulario de coordinación editable.
  // Se usa tanto al cargar el requerimiento como al cancelar la edición,
  // para descartar cambios sin guardar y volver al último estado guardado.
  const applyCoordFormFromData = useCallback((d) => {
    setCoordId(d?.id_cordinacion || "");
    setPlacas((d?.placa || "").split(/[,;\s]+/g).filter(Boolean).map(normalizePlate));
    setPlacaInput("");
    setTecnicosSel((d?.tecnico || "").split(/[,]+/g).map((s) => s.trim()).filter(Boolean));

    setForm({
      observacion: d?.obs2        ?? "",
      fecha:       isBogusDate(d?.fecha_cordinacion) ? "" : isoDateToYMD(d?.fecha_cordinacion),
      hora:        d?.hora_cordinacion ? fmtHora(d.hora_cordinacion) : "",
      lugar:       d?.lugar       ?? "",
      contacto:    d?.contacto    ?? "",
      telefono:    d?.telefono    ?? "",
    });

    const idTerc   = d?.tercia ?? d?.cod_servicio_cliente_tercializado ?? null;
    const flagTerc = d?.tercializado === 1 || d?.tercializado === "1" || d?.tercializado === true || !!idTerc;
    setTercializado(!!flagTerc);
    setTercError(false);
    if (flagTerc && idTerc) {
      setTercSelected({ cod_servicio: String(idTerc), razon_social: d?.razon_social_tercializado || `#${idTerc}` });
      fetchTercializados("");
    } else {
      setTercSelected(null);
    }
  }, [fetchTercializados]);

  const fetchData = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      setError(null); // FIX: limpiar error previo
      const resp = await getRequerimiento(cod);
      const d = resp?.data || null;
      setData(d);
      applyCoordFormFromData(d);

      if (d?.sucursal) await fetchTecnicos(d.sucursal);
      await fetchHistory(); // fetchData siempre carga history también
    } catch (e) {
      // FIX: error visible al usuario
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar el requerimiento");
    } finally {
      setLoading(false);
    }
  }, [cod, fetchTecnicos, fetchHistory, applyCoordFormFromData]);

  useEffect(() => { fetchData(); fetchProfile(); }, [fetchData, fetchProfile]);

  // FIX: refreshRequerimiento NO llama fetchHistory por separado — fetchData ya lo hace
  const refreshRequerimiento = useCallback(async () => {
    await fetchData({ silent: true });
    setRefreshKey((k) => k + 1);
  }, [fetchData]);

  // Placas del cliente
  const fetchPlacasCliente = useCallback(async (buscar = "") => {
    const csc = data?.cod_servicio_cliente;
    if (!requierePlacasCliente || !csc) { setPlacasCliente([]); return; }
    setPlacasClienteLoading(true);
    try {
      const { data: resp } = await getCatalogoPlacasVehiculos(csc, buscar, 20);
      const items = Array.isArray(resp?.data?.items)
        ? resp.data.items
        : Array.isArray(resp?.items)
        ? resp.items
        : Array.isArray(resp)
        ? resp
        : [];
      const seen = new Set();
      const normalized = items
        .map((item) => {
          if (typeof item === "string") {
            const placa = String(item).trim().toUpperCase();
            if (!placa || seen.has(placa)) return null;
            seen.add(placa);
            return { placa, clientes: [] };
          }
          const placa = String(item?.placa || "").trim().toUpperCase();
          if (!placa || seen.has(placa)) return null;
          seen.add(placa);
          return {
            ...item,
            placa,
            clientes: Array.isArray(item?.clientes) ? item.clientes : [],
          };
        })
        .filter(Boolean);
      setPlacasCliente(normalized);
    } catch {
      setPlacasCliente([]); // FIX: sin console.error ni console.debug
    } finally {
      setPlacasClienteLoading(false);
    }
  }, [data?.cod_servicio_cliente, requierePlacasCliente]);

  const buscarPlacasCliente = useMemo(
    () => debounce((buscar) => fetchPlacasCliente(buscar), 300),
    [fetchPlacasCliente]
  );

  useEffect(() => {
    fetchPlacasCliente("");
    return () => buscarPlacasCliente.cancel?.();
  }, [fetchPlacasCliente, buscarPlacasCliente]);

  // Tercia info
  useEffect(() => {
    if (!tercializado) return;
    const id  = data?.tercia ?? data?.cod_servicio_cliente_tercializado;
    const match = id ? tercOptions.find((o) => String(o.cod_servicio) === String(id)) : null;
    if (match) setTercSelected(match);
  }, [tercializado, data?.tercia, data?.cod_servicio_cliente_tercializado, tercOptions]);

  useEffect(() => {
    const id = data?.tercia ?? data?.cod_servicio_cliente_tercializado;
    if ((tercializado || isTercializado) && id) fetchTerciaInfo(String(id));
    else setTerciaInfo(null);
  }, [tercializado, isTercializado, data?.tercia, data?.cod_servicio_cliente_tercializado, fetchTerciaInfo]);

  // ─────────────────────────────────────────────
  // ACCIONES
  // ─────────────────────────────────────────────

  const handleCancelEdit = () => {
    applyCoordFormFromData(data);
    setOpenEdit(false);
  };

  const handleOpenEdit = useCallback(async () => {
    setOpenEdit(true);
    try {
      const { data: asignados } = await getAsignacionesModems(cod);
      setTieneEquiposAsignados(Array.isArray(asignados) && asignados.length > 0);
    } catch {
      setTieneEquiposAsignados(false);
    }
  }, [cod]);

  const handleSaveCoord = async () => {
    if (!canSave) { setToast({ open: true, sev: "error", msg: "Completa los campos obligatorios" }); return; }
    if (esAdicion && tercializado && !tercSelected) { setTercError(true); setToast({ open: true, sev: "error", msg: "Selecciona un cliente tercializado." }); return; }

    try {
      setSaving(true);
      const payloadCoord = {
        placa:       placas.join(","),
        tecnico:     tecnicosSel.join(", "),
        observacion: form.observacion || "",
        fecha:       form.fecha || "",
        hora:        form.hora  || "",
        lugar:       form.lugar || "",
        contacto:    form.contacto || "",
        telefono:    form.telefono || "",
      };

      const prevTercia = data?.tercia ?? data?.cod_servicio_cliente_tercializado ?? null;
      const nextTercia = esAdicion && tercializado && tercSelected ? Number(tercSelected.cod_servicio) : null;

      const calls = [updateCoordinacion(coordId, payloadCoord)];
      if (String(prevTercia ?? "") !== String(nextTercia ?? "")) {
        calls.push(updateRequerimiento(cod, { tercia: nextTercia }));
      }
      await Promise.all(calls);

      setToast({ open: true, sev: "success", msg: "Coordinación actualizada" });
      setOpenEdit(false);
      await fetchData({ silent: true });
      setRefreshKey((k) => k + 1);
    } catch {
      setToast({ open: true, sev: "error", msg: "No se pudo actualizar la coordinación" });
    } finally {
      setSaving(false);
    }
  };

  // "Instalar en vehículo": agrega a la coordinación una placa que faltaba (igual que la app).
  // Manda el resto de la coordinación tal como está guardada y recarga sin remontar la vista,
  // para que el formulario de instalar siga abierto. Devuelve la placa normalizada.
  const handleAgregarPlaca = useCallback(async (valor) => {
    const placa = normalizeNewPlate(valor);
    if (!isValidPlate(placa)) throw new Error("Formato no válido (1234-ABC, ABC-123 o BD09250)");
    const guardadas = (data?.placa || "").split(/[,;\s]+/g).filter(Boolean).map(normalizePlate);
    if (guardadas.includes(placa)) return placa;
    const fecha = isoDateToYMD(data?.fecha_cordinacion);
    const hora  = data?.hora_cordinacion ? fmtHora(data.hora_cordinacion) : "";
    if (!coordId || !fecha || !hora) throw new Error("La coordinación no tiene fecha u hora; edítala primero.");

    await updateCoordinacion(coordId, {
      placa:       [...guardadas, placa].join(","),
      tecnico:     (data?.tecnico || "").split(",").map((s) => s.trim()).filter(Boolean).join(", "),
      observacion: data?.obs2 ?? "",
      fecha,
      hora,
      lugar:       data?.lugar ?? "",
      contacto:    data?.contacto ?? "",
      telefono:    data?.telefono ?? "",
    });
    await fetchData({ silent: true });
    return placa;
  }, [data, coordId, fetchData]);

  // FIX: Dialog de confirmación en lugar de window.confirm
  const handleFinalizar = async () => {
    try {
      setFinalizando(true);
      await updateEstado(cod, { estado: 8, comentario: "Finalizado por el creador" });
      setToast({ open: true, sev: "success", msg: "Requerimiento finalizado" });
      setOpenFinalizar(false);
      await fetchData({ silent: true });
    } catch {
      setToast({ open: true, sev: "error", msg: "No se pudo finalizar" });
    } finally {
      setFinalizando(false);
    }
  };

  // Vista según tipo + estado
  const esTransferenciaPendiente = !!data?.es_transferencia_cliente && Number(data?.estado) === 2;
  const View = esTransferenciaPendiente
    ? ConfirmarTransferencia
    : getViewFor(data?.nombre_ot_tipo || "", data?.estado, data?.tipo_adicion ?? "", !!Number(data?.es_transferencia_cliente));

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  const content = (
    <Box
      sx={{
        color: "text.primary",
        "& .MuiButton-outlinedPrimary": {
          color: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.primary.light
              : theme.palette.primary.main,
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.primary.light
              : theme.palette.primary.main,
          "&:hover": {
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : theme.palette.primary.dark,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(45, 212, 191, 0.10)"
                : "rgba(0, 109, 119, 0.06)",
          },
        },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, rowGap: 1.5, mb: 2 }}
        onKeyDown={(e) => { if (e.altKey && e.key === "ArrowLeft") handleBack(); }}
      >
        <Tooltip title="Volver (Alt+←)">
          <IconButton size="small" aria-label="Volver" onClick={handleBack} sx={{ mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
          Requerimiento #{data?.cod ?? cod}
        </Typography>

        {estadoInfo && (
          <Chip label={estadoInfo.label} color={estadoInfo.color} size="small" />
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Refrescar">
          <IconButton size="small" onClick={() => { fetchData({ silent: true }); setRefreshKey((k) => k + 1); }}>
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Copiar enlace">
          <IconButton size="small" onClick={() => {
            const url = `${window.location.origin}/requerimiento/${cod}`;
            navigator.clipboard?.writeText(url);
            if (navigator.share) navigator.share({ title: `Requerimiento #${cod}`, url }).catch(() => {});
            setToast({ open: true, sev: "success", msg: "Enlace copiado" });
          }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Compartir por WhatsApp">
          <IconButton size="small" onClick={() => {
            const url = `${window.location.origin}/requerimiento/${cod}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(`Requerimiento #${cod} ${url}`)}`, "_blank", "noopener,noreferrer");
          }}>
            <WhatsAppIcon fontSize="small" color="success" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        {isEliminado && (
          <Typography variant="body2" color="error" sx={{ mr: 1, fontWeight: 600 }}>
            Requerimiento eliminado
          </Typography>
        )}

        {isCreator && (estadoNum === 7 || estadoNum === 5) && !isEliminado && (
          <Button variant="contained" color="success" onClick={() => setOpenFinalizar(true)}>
            Finalizar
          </Button>
        )}

        <Button variant="outlined" onClick={() => setOpenDetails(true)}>
          Detalles
        </Button>

        <Tooltip title={estadoNum === 8 ? "No se puede editar un requerimiento finalizado" : isEliminado ? "Requerimiento eliminado" : ""}>
          <span>
            <Button variant="contained" startIcon={<EditIcon />}
              onClick={handleOpenEdit} disabled={estadoNum === 8 || isEliminado}>
              Editar coordinación
            </Button>
          </span>
        </Tooltip>

        {!!data?.id_ot && (
          <Button size="small" endIcon={<LaunchIcon />} variant="outlined"
            onClick={() => navigate(`/orden-de-trabajo/${data.id_ot}`)}>
            Abrir O.T. #{data.id_ot}
          </Button>
        )}
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} /><Typography>Cargando…</Typography>
        </Box>
      ) : error ? (
        // FIX: error visible con reintentar
        <Alert severity="error" action={<Button size="small" onClick={fetchData}>Reintentar</Button>}>
          {error}
        </Alert>
      ) : !data ? (
        <Typography color="error">No se pudo cargar el requerimiento.</Typography>
      ) : (
        <Container maxWidth="lg" sx={{ minWidth: 0, px: { xs: 1, sm: 2 }, py: 0, pb: { xs: 8, md: 10 } }}>
          <View
            key={`${cod}-${refreshKey}`}
            cod={cod}
            estado={data?.estado}
            sucursal={data?.sucursal || ""}
            placas={placas}
            history={history}
            fmtFecha={fmtFecha}
            fmtHora={fmtHora}
            estadoMap={estadoMap}
            refreshKey={refreshKey}
            onToast={handleViewToast}
            tercia={data?.tercia ?? null}
            codServicioCliente={data?.cod_servicio_cliente ?? null}
            codCliente={data?.cod_cliente ?? null}
            tipoAdicion={data?.tipo_adicion ?? ""}
            clienteNombre={data?.razon_social ?? ""}
            servicioDescripcion={data?.descripcion ?? ""}
            onEditarCoordinacion={handleOpenEdit}
            onRefreshRequerimiento={refreshRequerimiento}
            onAgregarPlaca={handleAgregarPlaca}
          />
        </Container>
      )}

      {/* Dialog: Editar coordinación */}
      <EditCoordinacionDialog
        open={openEdit} onClose={handleCancelEdit} onSave={handleSaveCoord} saving={saving}
        cod={cod} coordId={coordId} estadoInfo={estadoInfo}
        form={form} setForm={setForm} phoneValid={phoneValid}
        placas={placas} setPlacas={setPlacas} placaInput={placaInput} setPlacaInput={setPlacaInput} invalids={invalids}
        tecnicos={tecnicos} tecnicosSel={tecnicosSel} setTecnicosSel={setTecnicosSel}
        esAdicion={esAdicion} tieneEquiposAsignados={tieneEquiposAsignados}
        tercializado={tercializado} setTercializado={setTercializado}
        tercOptions={tercOptions} tercLoading={tercLoading}
        tercSelected={tercSelected} setTercSelected={setTercSelected}
        tercError={tercError} setTercError={setTercError}
        fetchTercializados={fetchTercializados}
        requierePlacasCliente={requierePlacasCliente}
        placasCliente={placasCliente}
        placasClienteLoading={placasClienteLoading}
        buscarPlacasCliente={buscarPlacasCliente}
        tipo_requerimiento={data?.nombre_ot_tipo}
        razon_social={data?.razon_social}
        descripcion={data?.descripcion}
      />

      {/* Dialog: Detalles del requerimiento */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Detalles del requerimiento #{data?.cod}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Datos del Requerimiento</Typography>
          <Table size="small" sx={{ mb: 2, "& .MuiTableCell-root": { py: 0.5 } }}>
            <TableBody>
              <Row label="Tipo"           value={data?.nombre_ot_tipo} icon={null} />
              <Row label="Servicio"       value={data?.cod_servicio_cliente ? `#${data.cod_servicio_cliente}` : "—"} icon={null} />
              <Row label="Cliente"        value={data?.razon_social}   icon={null} />
              <Row label="Solicitante"    value={data?.solicitante}    icon={null} />
              <Row label="Sucursal"       value={data?.sucursal}       icon={null} />
              <Row label="Teléfono"       value={data?.telefono}       icon={<PhoneIcon fontSize="small" />} />
              <Row label="Tercializado"   value={tercValue}            icon={null} />
              <Row label="Emitido por"    value={data?.usu}            icon={null} />
              <Row label="Fecha creación" value={`${fmtFecha(data?.fecha)} ${fmtHora(data?.hora_registro)}`} icon={null} />
            </TableBody>
          </Table>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Coordinación</Typography>
          <Table size="small" sx={{ mb: 2, "& .MuiTableCell-root": { py: 0.5 } }}>
            <TableBody>
              <Row label="Fecha"       value={data?.fecha_cordinacion ? fmtFecha(data.fecha_cordinacion) : "—"} icon={<TodayIcon fontSize="small" />} />
              <Row label="Hora"        value={data?.hora_cordinacion  ? fmtHora(data.hora_cordinacion)   : "—"} icon={<ScheduleIcon fontSize="small" />} />
              <Row label="Técnico(s)"  value={data?.tecnico  || "—"}  icon={<EngineeringIcon fontSize="small" />} />
              <Row label="Contacto"    value={data?.contacto || "—"}  icon={<PersonIcon fontSize="small" />} />
              <Row label="Teléfono"    value={data?.telefono || "—"}  icon={<PhoneIcon fontSize="small" />} />
              <Row label="Observación" value={data?.obs2     || "—"}  icon={<NoteAltIcon fontSize="small" />} />
            </TableBody>
          </Table>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Placa(s)</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            {placas.length > 0
              ? placas.map((p) => (
                  <Chip key={p} size="small" label={p}
                    color={PLATE_REGEX.test(p) ? "primary" : "error"}
                    sx={{ height: 22, "& .MuiChip-label": { px: 0.75, fontSize: 12 } }} />
                ))
              : <Typography sx={{ fontSize: 12 }}>—</Typography>}
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Historial</Typography>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No hay eventos registrados para este requerimiento.
            </Typography>
          ) : (
            <Timeline
              sx={{
                p: 0,
                m: 0,
                "& .MuiTimelineOppositeContent-root": { flex: 0.28, py: 1 },
              }}
            >
              {history.map((h, idx) => {
                const titulo = h.tipo === "ESTADO"
                  ? `${estadoMap[Number(h.meta?.de)]?.label || h.meta?.de || "?"} → ${estadoMap[Number(h.meta?.a)]?.label || h.meta?.a || "?"}`
                  : h.titulo;
                return (
                  <TimelineItem key={idx}>
                    <TimelineOppositeContent color="text.secondary" sx={{ fontSize: 11 }}>
                      {h.fecha ? fmtFecha(h.fecha) : "—"}
                      <br />
                      {h.hora ? fmtHora(h.hora) : ""}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      {idx === history.length - 1 ? (
                        <TimelineDot color={HISTORY_TIPO_COLOR[h.tipo] || "grey"} />
                      ) : (
                        <TimelineDot variant="outlined" color="grey" />
                      )}
                      {idx < history.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{titulo}</Typography>
                      {h.detalle && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
                          {h.detalle}
                        </Typography>
                      )}
                      {h.tipo === "COORDINACION" && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {[h.meta?.lugar, h.meta?.tecnico, h.meta?.placa].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                      {h.actor && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Usuario: {h.actor}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* FIX: Dialog de confirmación — reemplaza window.confirm */}
      <Dialog open={openFinalizar} onClose={() => !finalizando && setOpenFinalizar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Finalizar requerimiento</DialogTitle>
        <DialogContent>
          <Typography>¿Deseas marcar el requerimiento <strong>#{cod}</strong> como FINALIZADO? Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFinalizar(false)} disabled={finalizando}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleFinalizar} disabled={finalizando}>
            {finalizando ? "Finalizando…" : "Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.sev} variant="filled" sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );

  return embedded ? content : <DashboardLayout>{content}</DashboardLayout>;
}
